import { useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Inbox } from 'lucide-react';
import { ColumnHeaderMenu, SortAction } from './ColumnHeaderMenu';
import {
  ColumnDef,
  ColumnId,
  DateFilter,
  DateFilterState,
  DisplayOptions,
  FilterState,
  FormatsState,
  SortState,
  TotalsState,
} from './types';
import { cellRule, COLOR_PRESETS, rowFormat } from './formats';
import { inlineTotal } from './totals';
import { ConditionalIcon } from './ConditionalIcon';
import { cn } from '@/lib/utils';
import type { ConditionalFormatRule } from './types';

const MIN_COLUMN_PX = 16;

interface ResizeHandleProps {
  currentWidth: number | undefined;
  onResize: (width: number) => void;
  onReset: () => void;
}

function ResizeHandle({ currentWidth, onResize, onReset }: ResizeHandleProps) {
  const draggingRef = useRef(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const th = (e.currentTarget as HTMLElement).parentElement as HTMLElement | null;
    const startWidth = currentWidth ?? (th ? th.getBoundingClientRect().width : 120);
    const startX = e.clientX;
    draggingRef.current = true;

    const onMove = (moveEv: MouseEvent) => {
      if (!draggingRef.current) return;
      const next = Math.max(MIN_COLUMN_PX, Math.round(startWidth + (moveEv.clientX - startX)));
      onResize(next);
    };
    const onUp = () => {
      draggingRef.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  return (
    <span
      onMouseDown={handleMouseDown}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onReset();
      }}
      className="absolute top-0 right-0 z-10 h-full w-1.5 -mr-px cursor-col-resize bg-transparent hover:bg-blue-300/60 active:bg-blue-400/80 transition-colors"
      title="Drag to resize · Double-click to reset"
    />
  );
}

/**
 * Build a Tailwind class string honoring the rule's per-channel bg/text colors.
 * Falls back to the legacy single `color` if the new channels aren't set.
 */
function ruleCellClass(rule: ConditionalFormatRule | null): string {
  if (!rule) return '';
  const bg = rule.bgColor ?? (rule.bgColor === undefined && rule.textColor === undefined ? rule.color : undefined);
  const text = rule.textColor ?? (rule.bgColor === undefined && rule.textColor === undefined ? rule.color : undefined);
  return cn(
    bg && COLOR_PRESETS[bg].bg,
    text && COLOR_PRESETS[text].text,
    (bg || text) && 'font-medium'
  );
}

type GroupMode = 'detail' | 'summary';

interface OperationsTableProps<T extends { id: string }> {
  rows: T[];
  allRows: T[];
  columns: ColumnDef<T>[];
  sort: SortState;
  filters: FilterState;
  dateFilters: DateFilterState;
  totals: TotalsState;
  formats: FormatsState;
  display: DisplayOptions;
  groupBy?: ColumnId;
  groupMode?: GroupMode;
  columnWidths?: Record<ColumnId, number>;
  onSort: (columnId: ColumnId, action: SortAction) => void;
  onFilterChange: (columnId: ColumnId, values: Set<string>) => void;
  onDateFilterChange: (columnId: ColumnId, df: DateFilter | null) => void;
  onToggleTotal: (columnId: ColumnId) => void;
  onOpenFormat: (columnId: ColumnId) => void;
  onSetGroupBy: (columnId: ColumnId | undefined) => void;
  onSetGroupMode: (mode: GroupMode) => void;
  onRenameColumn: (columnId: ColumnId, label: string | undefined) => void;
  onResizeColumn: (columnId: ColumnId, width: number | undefined) => void;
  pinnedFilterColumns: Set<ColumnId>;
  onTogglePinFilter: (columnId: ColumnId) => void;
  onRowClick?: (row: T) => void;
}

export function OperationsTable<T extends { id: string }>({
  rows,
  allRows,
  columns,
  sort,
  filters,
  dateFilters,
  totals,
  formats,
  display,
  groupBy,
  groupMode = 'detail',
  columnWidths,
  onSort,
  onFilterChange,
  onDateFilterChange,
  onToggleTotal,
  onOpenFormat,
  onSetGroupBy,
  onSetGroupMode,
  onRenameColumn,
  onResizeColumn,
  pinnedFilterColumns,
  onTogglePinFilter,
  onRowClick,
}: OperationsTableProps<T>) {
  const visibleColumns = columns.filter((c) => !display.hiddenColumns.has(c.id));
  const rowPadding =
    display.rowSpacing === 'loose'
      ? 'px-3 py-3'
      : display.rowSpacing === 'compact'
        ? 'px-2 py-0.5'
        : 'px-3 py-1';
  const hasAnyTotals = visibleColumns.some((c) => totals.has(c.id));
  // When any column has an explicit width, switch the table to fixed layout
  // so the browser respects the pixel widths instead of growing columns to fit
  // their content.
  const hasExplicitWidths = !!columnWidths && Object.keys(columnWidths).length > 0;

  const groupColumn = groupBy ? columns.find((c) => c.id === groupBy) ?? null : null;
  const groups = useMemo(() => {
    if (!groupColumn) return null;
    const map = new Map<string, T[]>();
    for (const row of rows) {
      const key = groupColumn.accessor(row) ?? '';
      const arr = map.get(key);
      if (arr) arr.push(row);
      else map.set(key, [row]);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [groupColumn, rows]);

  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const toggleGroup = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="bg-white border-t border-b border-stone-200">
      <div className="overflow-x-auto">
        <table
          className="min-w-full text-[13px]"
          style={hasExplicitWidths ? { tableLayout: 'fixed' } : undefined}
        >
          <thead className="bg-white border-b border-stone-200 sticky top-0 z-10 shadow-[0_1px_0_rgba(0,0,0,0.04)]">
            {hasAnyTotals && (
              <tr>
                {visibleColumns.map((col) => (
                  <td
                    key={col.id}
                    className="px-3 pt-1.5 pb-0 text-xs text-stone-600 font-medium whitespace-nowrap text-center"
                  >
                    {totals.has(col.id) ? inlineTotal(col, rows) : ''}
                  </td>
                ))}
              </tr>
            )}
            <tr>
              {visibleColumns.map((col) => {
                const uniqueValues = Array.from(
                  new Set(allRows.map((r) => col.accessor(r)))
                )
                  .filter((v) => v !== undefined && v !== null)
                  .sort();
                const explicitWidth = columnWidths?.[col.id];
                return (
                  <th
                    key={col.id}
                    style={explicitWidth ? { width: explicitWidth } : undefined}
                    className="text-left relative"
                  >
                    <ColumnHeaderMenu
                      column={col}
                      sort={sort}
                      filterValues={filters[col.id]}
                      dateFilter={dateFilters[col.id]}
                      uniqueValues={uniqueValues}
                      showTotal={totals.has(col.id)}
                      hasFormat={!!formats[col.id]}
                      isGroupedBy={groupBy === col.id}
                      groupMode={groupBy === col.id ? groupMode : undefined}
                      isPinnedToFilters={pinnedFilterColumns.has(col.id)}
                      onSort={(action) => onSort(col.id, action)}
                      onFilterChange={(values) => onFilterChange(col.id, values)}
                      onDateFilterChange={(df) => onDateFilterChange(col.id, df)}
                      onToggleTotal={() => onToggleTotal(col.id)}
                      onOpenFormat={() => onOpenFormat(col.id)}
                      onToggleGroupBy={() =>
                        onSetGroupBy(groupBy === col.id ? undefined : col.id)
                      }
                      onSetGroupMode={onSetGroupMode}
                      onRename={(label) => onRenameColumn(col.id, label)}
                      onTogglePinFilter={() => onTogglePinFilter(col.id)}
                    />
                    <ResizeHandle
                      currentWidth={explicitWidth}
                      onResize={(w) => onResizeColumn(col.id, w)}
                      onReset={() => onResizeColumn(col.id, undefined)}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length} className="py-16">
                  <div className="flex flex-col items-center gap-2 text-stone-400">
                    <Inbox className="w-7 h-7 stroke-[1.25]" />
                    <div className="text-sm font-medium text-stone-600">
                      Nothing to show here
                    </div>
                    <div className="text-xs text-stone-500">
                      Try clearing some filters.
                    </div>
                  </div>
                </td>
              </tr>
            ) : groups ? (
              groups.map(([groupKey, groupRows]) => {
                const isCollapsed =
                  groupMode === 'summary' ? true : collapsed.has(groupKey);
                return (
                  <GroupRows
                    key={groupKey}
                    groupKey={groupKey}
                    groupRows={groupRows}
                    visibleColumns={visibleColumns}
                    columns={columns}
                    formats={formats}
                    display={display}
                    totals={totals}
                    rowPadding={rowPadding}
                    columnWidths={columnWidths}
                    isCollapsed={isCollapsed}
                    isSummary={groupMode === 'summary'}
                    onToggle={() => toggleGroup(groupKey)}
                    onRowClick={onRowClick}
                  />
                );
              })
            ) : (
              rows.map((row, idx) => {
                const rowColor = rowFormat(row, columns, formats);
                const rowPalette = rowColor ? COLOR_PRESETS[rowColor] : null;
                const zebraStripe =
                  display.zebra && !rowPalette && idx % 2 === 1 ? 'bg-stone-50/60' : '';
                return (
                  <tr
                    key={row.id}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={cn(
                      'border-b border-stone-100 last:border-b-0 transition-colors',
                      onRowClick && 'cursor-pointer',
                      rowPalette
                        ? cn(rowPalette.bg, rowPalette.text)
                        : cn(zebraStripe, 'hover:bg-[#f3f9fd]')
                    )}
                  >
                    {visibleColumns.map((col) => {
                      const rule = cellRule(row, col, formats);
                      const cellClass = ruleCellClass(rule);
                      const accessorValue = col.accessor(row);
                      const baseContent = col.render
                        ? col.render(row)
                        : accessorValue || <span className="text-stone-300">—</span>;
                      const content = rule?.icon ? (
                        <span className="inline-flex items-center gap-1.5">
                          <ConditionalIcon
                            name={rule.icon}
                            color={rule.iconColor ?? rule.color}
                          />
                          {!rule.hideValue && <span>{baseContent}</span>}
                        </span>
                      ) : (
                        baseContent
                      );
                      const w = columnWidths?.[col.id];
                      return (
                        <td
                          key={col.id}
                          style={w ? { width: w, maxWidth: w } : undefined}
                          className={cn(
                            rowPadding,
                            w ? 'truncate overflow-hidden' : 'whitespace-nowrap',
                            cellClass || 'text-stone-900'
                          )}
                        >
                          {content}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between px-3 py-1.5 border-t border-stone-100 text-xs text-stone-500">
        <span>
          {rows.length === allRows.length
            ? `${rows.length} ${rows.length === 1 ? 'item' : 'items'}`
            : `${rows.length} of ${allRows.length} items`}
        </span>
      </div>
    </div>
  );
}

interface GroupRowsProps<T extends { id: string }> {
  groupKey: string;
  groupRows: T[];
  visibleColumns: ColumnDef<T>[];
  columns: ColumnDef<T>[];
  formats: FormatsState;
  display: DisplayOptions;
  totals: TotalsState;
  rowPadding: string;
  columnWidths?: Record<ColumnId, number>;
  isCollapsed: boolean;
  isSummary: boolean;
  onToggle: () => void;
  onRowClick?: (row: T) => void;
}

function GroupRows<T extends { id: string }>({
  groupKey,
  groupRows,
  visibleColumns,
  columns,
  formats,
  display,
  totals,
  rowPadding,
  columnWidths,
  isCollapsed,
  isSummary,
  onToggle,
  onRowClick,
}: GroupRowsProps<T>) {
  const hasAnyTotals = visibleColumns.some((c) => totals.has(c.id));
  const labelNode = (
    <span className="inline-flex items-center gap-1.5">
      {isSummary ? null : isCollapsed ? (
        <ChevronRight className="w-3.5 h-3.5 text-stone-500" />
      ) : (
        <ChevronDown className="w-3.5 h-3.5 text-stone-500" />
      )}
      <span className="text-stone-900 font-semibold">
        {groupKey || <span className="italic text-stone-400 font-normal">(empty)</span>}
      </span>
      <span className="text-stone-500 font-normal">({groupRows.length})</span>
    </span>
  );

  return (
    <>
      <tr
        onClick={isSummary ? undefined : onToggle}
        className={cn(
          'border-y border-stone-200 bg-stone-100/80',
          isSummary ? '' : 'hover:bg-stone-100 cursor-pointer'
        )}
      >
        {hasAnyTotals ? (
          visibleColumns.map((col, idx) => (
            <td
              key={col.id}
              className={cn(
                'px-3 py-1.5 text-xs font-semibold text-stone-700 whitespace-nowrap',
                idx > 0 && 'text-center font-medium text-stone-600'
              )}
            >
              {idx === 0
                ? labelNode
                : totals.has(col.id)
                  ? inlineTotal(col, groupRows)
                  : ''}
            </td>
          ))
        ) : (
          <td
            colSpan={visibleColumns.length}
            className="px-3 py-1.5 text-xs font-semibold text-stone-700 whitespace-nowrap"
          >
            {labelNode}
          </td>
        )}
      </tr>
      {!isCollapsed &&
        groupRows.map((row, idx) => {
          const rowColor = rowFormat(row, columns, formats);
          const rowPalette = rowColor ? COLOR_PRESETS[rowColor] : null;
          const zebraStripe =
            display.zebra && !rowPalette && idx % 2 === 1 ? 'bg-stone-50/60' : '';
          return (
            <tr
              key={row.id}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn(
                'border-b border-stone-100 last:border-b-0 transition-colors',
                onRowClick && 'cursor-pointer',
                rowPalette
                  ? cn(rowPalette.bg, rowPalette.text)
                  : cn(zebraStripe, 'hover:bg-stone-50')
              )}
            >
              {visibleColumns.map((col) => {
                const rule = cellRule(row, col, formats);
                const cellClass = ruleCellClass(rule);
                const accessorValue = col.accessor(row);
                const baseContent = col.render
                  ? col.render(row)
                  : accessorValue || <span className="text-stone-300">—</span>;
                const content = rule?.icon ? (
                  <span className="inline-flex items-center gap-1.5">
                    <ConditionalIcon
                      name={rule.icon}
                      color={rule.iconColor ?? rule.color}
                    />
                    {!rule.hideValue && <span>{baseContent}</span>}
                  </span>
                ) : (
                  baseContent
                );
                const w = columnWidths?.[col.id];
                return (
                  <td
                    key={col.id}
                    style={w ? { width: w, maxWidth: w } : undefined}
                    className={cn(
                      rowPadding,
                      w ? 'truncate overflow-hidden' : 'whitespace-nowrap',
                      cellClass || 'text-stone-900'
                    )}
                  >
                    {content}
                  </td>
                );
              })}
            </tr>
          );
        })}
    </>
  );
}
